
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import InfoModal from '@/components/modals/infoModal.vue';
import * as OM from '@/Model';

@Options({
    components: {}
})
export default class EditMaterialCompositionModal extends Vue {

    // Same tolerance as the server, whose check is Math.Round(Math.Abs(sum - 100), 6) > 0.01.
    // The epsilon is load-bearing: Math.abs(100 - 99.99) is 0.010000000000005116 in IEEE-754,
    // so a bare <= 0.01 would disable Save on a composition the server accepts.
    static readonly tolerance = 0.01 + 1e-9;

    @Prop() elementName: string;
    @Prop() availableMaterials: OM.RawMaterialConfigurationVM[];
    @Prop() allCountries: OM.TextIdentifier[];
    @Prop({ default: () => [] }) initialMaterials: OM.SelectedMaterialShare[];
    @Prop({ default: "" }) initialCountryIdentifier: string;
    @Prop({ default: false }) initialNoMaterial: boolean;
    @Prop({ default: false }) isReadOnly: boolean;
    @Prop() saveCallback: (payload: { materials: OM.MaterialShareVM[], countryIdentifier: string, noMaterial: boolean }) => Promise<any>;

    materials: OM.MaterialShareVM[] = [];
    countryIdentifier: string = "";
    noMaterial: boolean = false;

    tempMaterialIdentifier: string = "";
    tempPercentage: number = null;

    serverErrors: OM.MaterialCompositionError[] = [];

    created() {
        this.noMaterial = this.initialNoMaterial;
        this.countryIdentifier = this.initialCountryIdentifier;

        this.materials = (this.initialMaterials || []).map(share => {
            let vm = new OM.MaterialShareVM();
            vm.materialIdentifier = share.material.identifier;
            vm.materialName = share.material.text;
            vm.percentage = share.percentage;
            return vm;
        })
    }

    get selectableMaterials(): OM.RawMaterialConfigurationVM[] {
        // A material already in the table is absent from the dropdown, so it cannot be added twice.
        return this.availableMaterials
            .filter(x => !this.materials.some(m => m.materialIdentifier == x.identifier))
            .sort((a, b) => a.materialName.trim().localeCompare(b.materialName.trim(), undefined, { sensitivity: 'base' }));
    }

    get totalPercentage(): number {
        return this.materials.reduce((sum, x) => sum + (x.percentage || 0), 0);
    }

    get remainingPercentage(): number {
        let remaining = 100 - this.totalPercentage;
        return remaining < 0 ? 0 : remaining;
    }

    get totalOk(): boolean {
        return Math.abs(this.totalPercentage - 100) <= EditMaterialCompositionModal.tolerance;
    }

    get addDisabled(): boolean {
        if(this.isReadOnly)
            return true;
        return !this.tempMaterialIdentifier || !this.tempPercentage || this.tempPercentage <= 0 || this.remainingPercentage <= 0;
    }

    get isValid(): boolean {
        if(this.noMaterial)
            return true;
        return this.materials.length > 0 && this.totalOk && !!this.countryIdentifier;
    }

    get globalErrors(): string[] {
        return this.serverErrors.filter(x => !x.material).map(x => x.message);
    }

    rowErrors(materialName: string): string[] {
        return this.serverErrors.filter(x => x.material == materialName).map(x => x.message);
    }

    onNoMaterialChanged() {
        if(this.noMaterial) {
            this.materials = [];
            this.countryIdentifier = "";
            this.tempMaterialIdentifier = "";
            this.tempPercentage = null;
        }
        this.serverErrors = [];
    }

    addMaterial() {
        if(this.addDisabled)
            return;

        let material = this.availableMaterials.filter(x => x.identifier == this.tempMaterialIdentifier)[0];

        let vm = new OM.MaterialShareVM();
        vm.materialIdentifier = material.identifier;
        vm.materialName = material.materialName;
        // Clamp to what is left, so the user can always reach exactly 100 without arithmetic.
        vm.percentage = this.tempPercentage > this.remainingPercentage ? this.remainingPercentage : this.tempPercentage;

        this.materials.push(vm);

        this.tempMaterialIdentifier = "";
        this.tempPercentage = null;
        this.serverErrors = [];
    }

    removeMaterial(index: number) {
        this.materials.splice(index, 1);
        this.serverErrors = [];
    }

    save() {
        this.serverErrors = [];

        this.saveCallback({
            materials: this.materials,
            countryIdentifier: this.countryIdentifier,
            noMaterial: this.noMaterial
        })
        .catch(err => {
            this.serverErrors = err && err.errors ? err.errors : [];

            if(this.serverErrors.length == 0) {
                this.$opModal.show(InfoModal, {
                    text: this.$localizationService.getLocalizedValue("Error", "Error"),
                    subtitle: this.$localizationService.getLocalizedValue("Unable to save the materials", "Unable to save the materials"),
                    deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                    denyCb: () => {
                        this.$opModal.closeLast();
                    }
                })
            }
        })
    }
}
